import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'

import { ApiError } from '../api/client'
import { BUSINESS_TYPES, endpoints, type BusinessType, type CashierSettings } from '../api/endpoints'
import { businessTypeName as typeName } from '../businessTypes'
import { PageHeader } from '../components/Page'
import { PasswordField } from '../components/PasswordField'
import { PROFILE_DEFAULTS, profileChanges } from '../kasir'
import { formatPercent, formatRupiah } from '../money'
import { errorMessage, t } from '../strings'

/** A setting's value in words, for the "what will change" list. */
function describe(key: keyof typeof PROFILE_DEFAULTS.food, value: CashierSettings[keyof CashierSettings]): string {
  if (typeof value === 'boolean') return value ? t.kasirOn : t.kasirOff
  if (key === 'tax_rate_bps' || key === 'service_rate_bps') return `${formatPercent(Number(value))}%`
  if (key === 'takeaway_fee_amount') return formatRupiah(Number(value))
  if (key === 'rounding_unit') return value === 100 ? 'Rp 100' : t.kasirOff
  if (Array.isArray(value)) return value.length === 0 ? t.kasirOff : value.join(', ')
  return String(value)
}

/**
 * Ganti jenis usaha: a page of its own, reached from Info outlet (below Ganti kata sandi). The type changes the words and the settings
 * to that type's starting point; nothing is deleted (items, staff, sales, sessions and devices stay). The password is asked again, as for
 * closing the account: a borrowed screen must not change what the business is.
 */
export function ChangeBusinessTypePage() {
  const qc = useQueryClient()
  const settings = useQuery({ queryKey: ['cashier-settings'], queryFn: endpoints.cashierSettings })
  const [target, setTarget] = useState<BusinessType | null>(null)
  const [password, setPassword] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [done, setDone] = useState<BusinessType | null>(null)

  const apply = useMutation({
    mutationFn: ({ type, pw }: { type: BusinessType; pw: string }) => endpoints.applyProfile(type, pw),
    onSuccess: (next, { type }) => {
      qc.setQueryData(['cashier-settings'], next)
      void qc.invalidateQueries({ queryKey: ['business'] })
      setTarget(null)
      setPassword('')
      setDone(type)
    },
    onError: (e) => setFailure(e instanceof ApiError && e.code === 'wrong_password' ? t.deleteWrong : errorMessage(e instanceof ApiError ? e.code : 'network')),
  })

  const current = settings.data
  const others = current ? BUSINESS_TYPES.filter((b) => b !== current.business_type) : []
  const changes = current && target ? profileChanges(current, target) : []

  function submit(e: FormEvent) {
    e.preventDefault()
    setFailure(null)
    if (!target || !password) return
    apply.mutate({ type: target, pw: password })
  }

  return (
    <>
      <PageHeader title={t.typeTitle} subtitle={t.typeSub} back={{ to: '/pengaturan', label: t.outletInfo }} />
      <div className="narrow">
        {settings.isError && <p className="error">{errorMessage(settings.error.message)}</p>}
        {!current && !settings.isError && <p>{t.loading}</p>}
        {current && (
          <section className="card">
            <p>
              <strong>{t.typeNow(typeName(current.business_type))}</strong>
            </p>
            {done && (
              <p className="ok" role="status">
                {t.typeDone(typeName(done))}
              </p>
            )}
            <h2>{t.typePick}</h2>
            <div className="stack">
              {others.map((other) => (
                <button
                  key={other}
                  type="button"
                  className={target === other ? 'secondary on' : 'secondary'}
                  aria-pressed={target === other}
                  onClick={() => {
                    setTarget(other)
                    setFailure(null)
                    setDone(null)
                  }}
                >
                  {t.kasirSwitchTo(typeName(other))}
                </button>
              ))}
            </div>

            {target && (
              <form onSubmit={submit} className="type-confirm">
                <h2>{t.typeChangesTitle(typeName(target))}</h2>
                {changes.length === 0 ? (
                  <p>{t.kasirApplyNothing}</p>
                ) : (
                  <>
                    <p className="muted small">{t.kasirApplyConfirmBody}</p>
                    <ul>
                      {changes.map((k) => (
                        <li key={k}>
                          {t.kasirFieldNames[k]}: {describe(k, current[k])} → {describe(k, PROFILE_DEFAULTS[target][k])}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <p className="notice">{t.typeKept}</p>
                <PasswordField label={t.deletePassword} autoComplete="current-password" value={password} onChange={setPassword} />
                <button type="submit" disabled={apply.isPending || !password}>
                  {t.typeButton}
                </button>
                {failure && (
                  <p className="error" role="alert">
                    {failure}
                  </p>
                )}
              </form>
            )}
          </section>
        )}
      </div>
    </>
  )
}
